import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import {
  ChakraFlex,
  ChakraImage,
  ChakraSpacer,
  ChakraText,
  ChatUser,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Reply,
  SkeletonLoader,
  TopicToArrow,
  UpDownArrow,
} from '@equip.health/ui';
import { User } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import {
  FileMessage,
  ThreadInfo,
  UserMessage,
  MessageTypeFilter,
} from '@sendbird/chat/message';
import { useChannelContext } from '@sendbird/uikit-react/Channel/context';
import EmojiReactions from '@sendbird/uikit-react/ui/EmojiReactions';
import MessageItemReactionMenu from '@sendbird/uikit-react/ui/MessageItemReactionMenu';
import Tooltip from '@sendbird/uikit-react/ui/Tooltip';
import TooltipWrapper from '@sendbird/uikit-react/ui/TooltipWrapper';
import { isEmpty } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import OverviewLoading from '~/components/common/OverviewLoading';
import RawHTML from '~/components/common/RawHTML';
import {
  ChannelId,
  MEMBERS_TO_DISPLAY_IN_REPLY,
} from '~/lib/constants/chat.constants';
import { S3FileStatus } from '~/lib/constants/s3filestatus.contants';
import { useNoteAttachmentModalContext } from '~/lib/contexts/NoteAttachmentModalContext';
import useChatAttachment from '~/lib/hooks/chat/useChatAttachment';
import { ChatMessage, FileAttachment, MemberMetaData } from '~/lib/types/chat';
import ChatUtil, {
  getMentionsBoxReplyPlacholderText,
  isImage,
  removeUUIDFromFilename,
} from '~/lib/util/chat/chat.utils';

import AttachmentViewer, { PreviewDocument } from './AttachmentViewer';
import MentionsBox from './MentionsBox';
import MessageKebabMenu from './MessageKebabMenu';
import MessageDotIndicator from './NewMessageIndicator';
import ChannelMembers from '../../common/ChannelMembers';

import './sendbird-reactions.css';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import PatientMyMessagePin from './PatientMyMessagePin';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import AnalyticsEvents from '~/analytics';
import { MessagesEventsProps } from '~/analytics/messagesEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

const idPrefix = 'message';
const idPostfix = 'tile';

const { h6, label, labelBold, paragraph } = EquipUITheme.typography;
const { primary } = EquipUIFireflyV2Theme.colors;

const getSpaceFromTrigger = (fileMessage: FileMessage) => {
  return { x: 0, y: fileMessage ? 5 : -90 };
};

interface MessageReplyRowProps {
  id: string;
  isShowReply: boolean;
  members: ChatUser[];
  onClick: () => void;
}

const MessageReplyRow = ({
  id,
  isShowReply,
  members,
  onClick,
}: MessageReplyRowProps) => {
  const hasMembers = (members ?? []).length > 0;
  return (
    isShowReply && (
      <ChakraFlex
        alignItems="center"
        gridGap="4px"
        id={`${idPrefix}-reply-container__${id}__${idPostfix}`}
        marginTop="12px"
        width="100%"
      >
        <>
          {hasMembers && (
            <ChakraFlex
              alignItems="center"
              gridGap="4px"
              id={`${idPrefix}-reply-wrapper__${id}__${idPostfix}`}
              paddingRight="14px"
            >
              <TopicToArrow
                id={`${idPrefix}-reply-topic-to-arrow__${id}__${idPostfix}`}
              />
              <ChakraText
                color="neutral.secondary"
                {...label}
                id={`${idPrefix}-message-to-label__${id}__${idPostfix}`}
              >
                Message to
              </ChakraText>
              {(members ?? [])
                .slice(0, MEMBERS_TO_DISPLAY_IN_REPLY)
                .map((member) => (
                  <>
                    <ChannelMembers
                      id={id}
                      members={[member]}
                      paddedTop={false}
                    />
                    <ChakraText
                      color="neutral.secondary"
                      {...label}
                      id={`${idPrefix}-member-nickname__${member?.userId}__${idPostfix}`}
                    >
                      {member?.nickname}
                    </ChakraText>
                  </>
                ))}
              {(members ?? []).length > MEMBERS_TO_DISPLAY_IN_REPLY && (
                <ChannelMembers
                  id={id}
                  members={members.slice(
                    MEMBERS_TO_DISPLAY_IN_REPLY,
                    members.length,
                  )}
                  paddedTop={false}
                />
              )}
            </ChakraFlex>
          )}
          <ChakraFlex
            cursor="pointer"
            gridGap="4px"
            id={`${idPrefix}-reply-label-wrapper__${id}__${idPostfix}`}
            onClick={onClick}
          >
            <ChakraText
              color="primary.100"
              {...labelBold}
              id={`${idPrefix}-reply-label__${id}__${idPostfix}`}
            >
              Reply
            </ChakraText>
            <Reply
              fill="teal"
              height="16px"
              id={`${idPrefix}-reply-icon__${id}__${idPostfix}`}
              width="16px"
            />
          </ChakraFlex>
        </>
      </ChakraFlex>
    )
  );
};

interface DocumentMessageProps {
  content: FileMessage;
  id: string;
  isShowKebabMenu: boolean;
  isThreadedMessage: boolean;
  onMessageRemoved: (isRemoved: boolean) => void;
}

const isDownloadableFileValidationStatus = (fileValidationStatus: string) => {
  return [
    S3FileStatus.NEW.toString(),
    S3FileStatus.UNKNOWN.toString(),
    S3FileStatus.CLEAN.toString(),
  ].includes(fileValidationStatus);
};

const DocumentMessage = ({
  content,
  id,
  isShowKebabMenu,
  isThreadedMessage,
  onMessageRemoved,
}: DocumentMessageProps) => {
  const {
    fileValidationStatus,
    removeAttachment,
    s3Url,
    getDocumentDownloadURL,
  } = useChatAttachment();

  const { currentPatientId } = useGlobalContext();

  const [isImageDocumentClicked, setIsImageDocumentClicked] =
    useState<boolean>(false);

  const [isStatusFetched, setIsStatusFetched] = useState<boolean>(false);
  const { setShowNoteAttachmentModal } = useNoteAttachmentModalContext();

  const isImageAttachment = isImage(content?.name);

  const isValidFile = useMemo(() => {
    return (
      isDownloadableFileValidationStatus(fileValidationStatus) &&
      !isEmpty(s3Url)
    );
  }, [s3Url, fileValidationStatus]);

  const isInvalidFile = useMemo(() => {
    return (
      isEmpty(fileValidationStatus) ||
      [S3FileStatus.INVALID.toString()].includes(fileValidationStatus)
    );
  }, [fileValidationStatus]);

  useEffect(() => {
    if (fileValidationStatus) setIsStatusFetched(true);
  }, [fileValidationStatus]);

  useEffect(() => {
    if (isValidFile && isImageDocumentClicked && isStatusFetched) {
      if (s3Url) {
        setShowNoteAttachmentModal({
          name: content?.name,
          url: s3Url,
        });
      }
      setIsImageDocumentClicked(false);
    } else if (
      (isImageDocumentClicked || isImageAttachment) &&
      !isStatusFetched
    ) {
      getDocumentDownloadURL({
        fileName: content?.name,
        patientExternalId: currentPatientId,
        onSuccess: () => {
          onMessageRemoved(false);
        },
        onFailure: () => {
          onMessageRemoved(true);
        },
      });
    }
  }, [isImageDocumentClicked, isStatusFetched]);

  const handleRemoveMessage = () => {
    removeAttachment(
      content?.name,
      () => {
        onMessageRemoved(true);
      },
      () => {
        onMessageRemoved(false);
      },
    );
  };

  if (!s3Url && isImageAttachment && !fileValidationStatus) {
    return <SkeletonLoader boxSize="180px" id={idPrefix} />;
  }

  return (
    <ChakraFlex
      id={`${idPrefix}-message-content-container__${id}__${idPostfix}`}
      overflow="hidden"
    >
      {isImageAttachment && !isInvalidFile ? (
        <ChakraFlex alignItems="baseline">
          <ChakraImage
            alt={content?.name}
            borderRadius="16px"
            boxSize="150px"
            cursor="pointer"
            fit="cover"
            id={`${idPrefix}-message-content__${id}__${idPostfix}`}
            loading="lazy"
            onClick={() => {
              if (isDownloadableFileValidationStatus(fileValidationStatus)) {
                setIsImageDocumentClicked(true);
              }
            }}
            src={s3Url}
            title={removeUUIDFromFilename(content?.name)}
          />
          {!isThreadedMessage && isShowKebabMenu && (
            <MessageKebabMenu
              messageId={content.messageId}
              onRemoveClicked={handleRemoveMessage}
            />
          )}
        </ChakraFlex>
      ) : (
        <ChakraFlex
          alignItems="center"
          backgroundColor="#F2F2F2"
          border="1px solid #ccc"
          borderRadius="10px"
          width="360px"
        >
          <PreviewDocument
            file={content?.name}
            fileValidationStatus={fileValidationStatus}
            isLoading={false}
            isShowClose={false}
            isValidFile={isValidFile}
            onClick={() => {
              if (S3FileStatus.INVALID !== fileValidationStatus) {
                setIsImageDocumentClicked(true);
              }
            }}
          />
          {!isThreadedMessage && isShowKebabMenu && (
            <MessageKebabMenu
              messageId={content.messageId}
              onRemoveClicked={handleRemoveMessage}
            />
          )}
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

interface MessageContentProps {
  content: string;
  id: string;
}
const MessageContent = ({ content, id }: MessageContentProps) => (
  <ChakraText
    {...paragraph}
    id={`${idPrefix}-message-content-container__${id}__${idPostfix}`}
  >
    <RawHTML
      content={content}
      id={`${idPrefix}-message-content__${id}__${idPostfix}`}
    />
  </ChakraText>
);

interface MessageHeaderRowProps {
  createdDate: string;
  id: string;
  isMentioned: boolean;
  isRead: boolean;
  isReverse?: boolean;
  senderName: string;
  timestamp: string;
}

const MessageHeaderRow = React.forwardRef<
  HTMLDivElement,
  MessageHeaderRowProps
>(
  (
    {
      createdDate,
      id,
      isMentioned,
      isRead,
      isReverse = false,
      senderName,
      timestamp,
    }: MessageHeaderRowProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => (
    <ChakraFlex
      alignItems="start"
      direction={isReverse ? 'row-reverse' : 'row'}
      gridGap="8px"
      id={`${idPrefix}-header-container__${id}__${idPostfix}`}
      ref={ref}
      width="100%"
    >
      <ChakraText
        {...h6}
        id={`${idPrefix}-header-sender-name__${id}__${idPostfix}`}
      >
        {senderName}
      </ChakraText>
      {createdDate && (
        <ChakraText
          color="neutral.secondary"
          textTransform="capitalize"
          {...label}
          id={`${idPrefix}-header-created-date__${id}__${idPostfix}`}
        >
          {createdDate}
        </ChakraText>
      )}
      <ChakraText
        color="neutral.secondary"
        {...label}
        id={`${idPrefix}-header-timestamp__${id}__${idPostfix}`}
      >
        {timestamp}
      </ChakraText>
      {!isRead && (
        <MessageDotIndicator
          id={`${idPrefix}-header-new-message-indicator__${id}__${idPostfix}`}
        />
      )}
      <ChakraSpacer />
      {!isRead && isMentioned && (
        <MessageDotIndicator
          id={`${idPrefix}-header-new-mentions-indicator__${id}__${idPostfix}`}
          isMentioned={isMentioned}
        />
      )}
    </ChakraFlex>
  ),
);

MessageHeaderRow.displayName = 'MessageHeaderRow';

interface AdminMessageProps {
  avatarSize: AvatarSize;
  id: string;
  isRead: boolean;
  message: string;
  sender: User;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const AdminMessage = React.forwardRef<HTMLDivElement, AdminMessageProps>(
  (
    { avatarSize, id, isRead, message, sender, unreadRef }: AdminMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const messageContainerProps = {
      alignItems: 'center',
      borderRadius: '16px',
      borderWidth: '1px',
      gridGap: '12px',
      marginBottom: '24px',
      padding: '12px',
      width: '100%',
    };

    const { nickname, metaData } = sender;
    const senderRole = ((metaData ?? {}) as MemberMetaData)?.providerType;

    return (
      <ChakraFlex
        id={`${idPrefix}-admin-message-container__${id}__${idPostfix}`}
        ref={ref}
        {...messageContainerProps}
      >
        {nickname && (
          <ChannelMembers
            id={id}
            members={[sender] as ChatUser[]}
            paddedTop={false}
            showPopoverOnHover
            size={avatarSize}
          />
        )}
        <ChakraFlex
          alignItems="center"
          id={`${idPrefix}-sender-role-container__${id}__${idPostfix}`}
          ref={unreadRef}
          width="100%"
        >
          {nickname && (
            <ChakraFlex
              direction="column"
              id={`${idPrefix}-sender-role-wrapper__${id}__${idPostfix}`}
            >
              <ChakraText
                {...h6}
                data-testid={`${idPrefix}-sender-nickname__${id}__${idPostfix}`}
                id={`${idPrefix}-sender-nickname__${id}__${idPostfix}`}
              >
                {nickname}
              </ChakraText>
              {senderRole && (
                <ChakraText
                  color="neutral.secondary"
                  {...paragraph}
                  data-testid={`${idPrefix}-sender-role__${id}__${idPostfix}`}
                  id={`${idPrefix}-sender-role__${id}__${idPostfix}`}
                >
                  {senderRole}
                </ChakraText>
              )}
            </ChakraFlex>
          )}
          {!isRead && (
            <MessageDotIndicator
              id={`${idPrefix}-new-message__${id}__${idPostfix}`}
            />
          )}
          <ChakraSpacer />
          <ChakraText
            color="neutral.secondary"
            {...label}
            data-testid={`${idPrefix}-admin__${id}__${idPostfix}`}
            id={`${idPrefix}-admin__${id}__${idPostfix}`}
          >
            {message}
          </ChakraText>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
AdminMessage.displayName = 'AdminMessage';

interface SingleMessageProps {
  avatarSize: AvatarSize;
  chatUserId: string;
  channel: GroupChannel;
  children?: ReactNode;
  createdAt: number;
  fileMessage?: FileMessage;
  chatMessage?: UserMessage | FileMessage;
  id: string;
  isChannelReadOnly: boolean;
  isMentioned: boolean;
  isMessageSent: boolean;
  isRead: boolean;
  isShowKebabMenu: boolean;
  isShowReply: boolean;
  isThreadedMessage: boolean;
  mentionedUsers: User[];
  message: string;
  noMargin?: boolean;
  onMessageRemoved: (isRemoved: boolean) => void;
  onSendMessage?: (value: ChatMessage) => void;
  patientId: string;
  referenceDate?: number;
  sender: User;
  timestamp: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const SingleMessage = React.forwardRef<HTMLDivElement, SingleMessageProps>(
  (
    {
      avatarSize,
      chatUserId,
      channel,
      chatMessage,
      children,
      createdAt,
      fileMessage,
      id,
      isChannelReadOnly,
      isMentioned,
      isMessageSent,
      isRead,
      isShowKebabMenu,
      isShowReply,
      isThreadedMessage,
      mentionedUsers,
      message,
      noMargin = false,
      onMessageRemoved,
      onSendMessage,
      referenceDate,
      sender,
      timestamp,
      unreadRef,
    }: SingleMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      maudProviderChatMessagesPrototype:
        isMaudProviderChatMessagesPrototypeEnabled,
      messagePinning: isMessagePinningEnabled,
    } = useFlags<LDFlags>();
    const { emojiContainer, toggleReaction } = useChannelContext();
    const { trackEvent } = useAnalytics();

    const hasMessageReactions = chatMessage?.reactions.length > 0;
    const memberNicknamesMap = new Map(
      channel?.members.map((obj) => [obj.userId, obj.nickname]),
    );
    const singleMessageContainerProps = {
      alignItems: 'start',
      gridGap: '16px',
      marginBottom: noMargin ? '' : '24px',
      width: '100%',
    };
    const { url, members } = channel;
    const [isShowReplyInput, setIsShowReplyInput] = useState<boolean>(false);
    const [fileToSend, setFileToSend] = useState<FileAttachment>(null);
    const { showErrorToast } = useErrorToast();
    const calculatedDate =
      ChatUtil.calculateRelativeDate(referenceDate, true) !==
      ChatUtil.calculateRelativeDate(createdAt, true)
        ? ChatUtil.calculateRelativeDate(createdAt, true)
        : null;
    const [isPinLoading, setIsPinLoading] = useState<boolean>(false);
    const [isMessagePinned, setIsMessagePinned] = useState<boolean>(
      channel?.pinnedMessageIds?.includes(chatMessage.messageId),
    );
    const pinOrUnpinText = `${isMessagePinned ? 'Unpin' : 'Pin'} message`;

    const onTogglePin = (messageId: number) => {
      const messageType = fileMessage
        ? MessageTypeFilter.FILE
        : MessageTypeFilter.USER;

      const getPatientIdFromChannelUrl = (channelUrl: string) => {
        const splitted = channelUrl.split(/[_]+/g);
        if (channelUrl.startsWith('ACT')) return splitted[1];
        else if (channelUrl.startsWith('PT_PR_CCT')) return splitted[3];
        else return splitted[2];
      };

      setIsPinLoading(true);

      const eventProps: MessagesEventsProps = {
        channelType: channel.url.split('_')[0],
        channelUrl: channel.url,
        messageId: messageId.toString(),
        messageType,
        patientId: getPatientIdFromChannelUrl(channel.url),
        source: AnalyticsSourceValues.PATIENT_MESSAGES_TAB,
      };

      if (isMessagePinned) {
        trackEvent(
          AnalyticsEvents.patientChartMessageChannelUnpinMessage,
          eventProps,
        );

        channel
          .unpinMessage(messageId)
          .then(() => setIsMessagePinned(false))
          .catch(() =>
            showErrorToast(
              GlobalErrorCode.UNPIN_MESSAGE,
              messageId,
              'Failed to unpin message',
            ),
          )
          .finally(() => setIsPinLoading(false));
      } else {
        trackEvent(
          AnalyticsEvents.patientChartMessageChannelPinMessage,
          eventProps,
        );
        channel
          .pinMessage(messageId)
          .then(() => setIsMessagePinned(true))
          .catch(() =>
            showErrorToast(
              GlobalErrorCode.UNPIN_MESSAGE,
              messageId,
              'Failed to pin message',
            ),
          )
          .finally(() => setIsPinLoading(false));
      }
    };

    return (
      <ChakraFlex
        id={`${idPrefix}-single-message-container__${id}__${idPostfix}`}
        ref={ref}
        {...singleMessageContainerProps}
      >
        <ChannelMembers
          id={`${idPrefix}-single-message-members__${id}__${idPostfix}`}
          members={[sender] as ChatUser[]}
          paddedTop={false}
          showPopoverOnHover
          size={avatarSize}
        />
        <ChakraFlex
          direction="column"
          id={`${idPrefix}-single-message-wrapper__${id}__${idPostfix}`}
          width="100%"
        >
          <MessageHeaderRow
            createdDate={calculatedDate}
            id={`${idPrefix}-single-message-header-row__${id}__${idPostfix}`}
            isMentioned={isMentioned}
            isRead={isRead}
            ref={unreadRef}
            senderName={sender?.nickname}
            timestamp={timestamp}
          />
          <ChakraFlex
            _hover={{ background: '#ffffff !important' }}
            className="sendbird-parent-message-info"
            direction="column"
            id={`${idPrefix}-single-message-column-wrapper__${id}__${idPostfix}`}
            padding="5px 10px 5px 10px"
            width="100%"
          >
            <ChakraFlex
              direction="row"
              id={`${idPrefix}-single-message-row-wrapper__${id}__${idPostfix}`}
            >
              {fileMessage ? (
                <DocumentMessage
                  content={fileMessage}
                  id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                  isShowKebabMenu={isShowKebabMenu}
                  isThreadedMessage={isThreadedMessage}
                  onMessageRemoved={onMessageRemoved}
                />
              ) : (
                <MessageContent
                  content={message}
                  id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                />
              )}
              {isMaudProviderChatMessagesPrototypeEnabled &&
                isMessagePinningEnabled &&
                !isChannelReadOnly && (
                  <TooltipWrapper
                    className="sb-tooltip-incoming"
                    hoverTooltip={<Tooltip>{pinOrUnpinText}</Tooltip>}
                  >
                    <PatientMyMessagePin
                      chatMessage={chatMessage}
                      id={id}
                      isMessagePinned={isMessagePinned}
                      isPinLoading={isPinLoading}
                      messageType="incoming"
                      onTogglePin={onTogglePin}
                    />
                  </TooltipWrapper>
                )}
              {!isChannelReadOnly && !hasMessageReactions && (
                <TooltipWrapper
                  className="sb-tooltip-incoming"
                  hoverTooltip={<Tooltip>Add reaction</Tooltip>}
                >
                  <MessageItemReactionMenu
                    className="sendbird-parent-message-info__reaction-menu sb-reaction-menu-incoming sendbird-mouse-hover"
                    emojiContainer={emojiContainer}
                    key={chatMessage?.messageId}
                    message={chatMessage}
                    spaceFromTrigger={getSpaceFromTrigger(fileMessage)}
                    toggleReaction={toggleReaction}
                    userId={chatUserId}
                  />
                </TooltipWrapper>
              )}
            </ChakraFlex>
            {!isChannelReadOnly && hasMessageReactions && (
              <ChakraFlex
                data-testid={`${id}__emoji-reactions-wrapper`}
                id={`${id}__emoji-reactions-wrapper`}
              >
                {/* Doesn't have supposedHover capabilty */}
                <EmojiReactions
                  channel={channel}
                  emojiContainer={emojiContainer}
                  memberNicknamesMap={memberNicknamesMap}
                  message={chatMessage}
                  spaceFromTrigger={getSpaceFromTrigger(null)}
                  toggleReaction={toggleReaction}
                  userId={chatUserId}
                />
              </ChakraFlex>
            )}
          </ChakraFlex>
          <MessageReplyRow
            id={`${idPrefix}-single-message-reply__${id}__${idPostfix}`}
            isShowReply={isShowReply && !children && !fileMessage}
            members={mentionedUsers as ChatUser[]}
            onClick={() => setIsShowReplyInput(!isShowReplyInput)}
          />
          {isShowReplyInput && (
            <ChakraFlex
              id={`${idPrefix}-single-message-mentions-box-wrapper__${id}__${idPostfix}`}
              marginTop="10px"
              width="100%"
            >
              <MentionsBox
                autofocus
                channelUrl={url}
                id={`${idPrefix}-single-message-mentions-box__${id}__${idPostfix}`}
                isMessageSent={isMessageSent}
                key={id}
                members={members as ChatUser[]}
                onEnterPressed={(newMessage) => {
                  onSendMessage(newMessage);
                  setFileToSend(null);
                }}
                onFileSelected={(selectedFile) => {
                  setFileToSend(selectedFile);
                }}
                placeholder={getMentionsBoxReplyPlacholderText(
                  true,
                  isMaudProviderChatMessagesPrototypeEnabled,
                )}
                updatedAttachment={fileToSend}
              />
            </ChakraFlex>
          )}
          {fileToSend && (
            <AttachmentViewer
              attachments={[fileToSend.file]}
              id={id}
              isLoading={isMessageSent}
              onRemove={() => {
                setFileToSend(null);
              }}
            />
          )}
          {children}
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
SingleMessage.displayName = 'SingleMessage';

interface ThreadedMessageProps {
  avatarSize: AvatarSize;
  chatUserId: string;
  channel: GroupChannel;
  chatMessage: FileMessage | UserMessage;
  createdAt: number;
  fileMessage?: FileMessage;
  id: string;
  isChannelReadOnly: boolean;
  isMentioned: boolean;
  isMessageSent: boolean;
  isNewReply: boolean;
  isRead: boolean;
  isShowKebabMenuForReplies: boolean;
  isShowReply: boolean;
  mentionedUsers: User[];
  message: string;
  messageId: number;
  onInputFocus: (inputRef: React.MutableRefObject<HTMLInputElement>) => void;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
  onRepliesExpanded?: () => void;
  onSendMessage?: (value: ChatMessage) => void;
  patientId: string;
  replies: ChatMessage[];
  sender: User;
  threadInfo: ThreadInfo;
  timestamp: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const ThreadedMessage = React.forwardRef<HTMLDivElement, ThreadedMessageProps>(
  (props: ThreadedMessageProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { url, members } = props.channel;
    const [fileToSend, setFileToSend] = useState<FileAttachment>(null);
    const { trackEvent } = useAnalytics();
    const {
      maudProviderChatMessagesPrototype:
        isMaudProviderChatMessagesPrototypeEnabled,
    } = useFlags<LDFlags>();

    const handleExpandClick = () => {
      const newExpandedState = !isExpanded;
      setIsExpanded(newExpandedState);

      if (newExpandedState && !isExpanded) {
        const getPatientIdFromChannelUrl = (channelUrl: string) => {
          const splitted = channelUrl.split(/[_]+/g);
          if (channelUrl.startsWith('ACT')) return splitted[1];
          else if (channelUrl.startsWith('PT_PR_CCT')) return splitted[3];
          else return splitted[2];
        };

        const eventProps: MessagesEventsProps = {
          channelType: props.channel.url.split('_')[0],
          channelUrl: props.channel.url,
          messageId: props.messageId.toString(),
          patientId: getPatientIdFromChannelUrl(props.channel.url),
          source: AnalyticsSourceValues.PATIENT_MESSAGES_TAB,
        };

        trackEvent(
          AnalyticsEvents.patientChartMessageChannelExpandThread,
          eventProps,
        );
        props.onRepliesExpanded?.();
      } else if (newExpandedState) {
        props.onRepliesExpanded?.();
      }
    };

    return (
      <SingleMessage
        avatarSize={props.avatarSize}
        channel={props.channel}
        chatMessage={props.chatMessage}
        chatUserId={props.chatUserId}
        createdAt={props.createdAt}
        fileMessage={props.fileMessage}
        id={`${idPrefix}-threaded-message-main__${props.id}__${idPostfix}`}
        isChannelReadOnly={props.isChannelReadOnly}
        isMentioned={props.isMentioned}
        isMessageSent={props.isMessageSent}
        isRead={props.isRead}
        isShowKebabMenu={props.isShowKebabMenuForReplies}
        isShowReply={false}
        isThreadedMessage
        mentionedUsers={props.mentionedUsers}
        message={props.message}
        onMessageRemoved={() => props.onMessageRemoved(props.messageId)}
        onSendMessage={props.onSendMessage}
        patientId={props.patientId}
        ref={ref}
        referenceDate={props.createdAt}
        sender={props.sender}
        timestamp={props.timestamp}
        unreadRef={props.unreadRef}
      >
        <ChakraFlex
          alignItems="start"
          borderColor="neutral.line"
          borderRadius="16px"
          borderWidth="1px"
          direction="column"
          id={`${idPrefix}-threaded-message-container__${props.id}__${idPostfix}`}
          padding="13px"
        >
          <ChakraFlex
            alignItems="center"
            cursor="pointer"
            gridGap="8px"
            id={`${idPrefix}-threaded-message-wrapper__${props.id}__${idPostfix}`}
            onClick={handleExpandClick}
            padding="6px"
            width="100%"
          >
            <Reply
              height="16px"
              id={`${idPrefix}-threaded-message-reply__${props.id}__${idPostfix}`}
              width="16px"
            />
            {props.threadInfo?.replyCount > 0 && (
              <ChakraText
                color="neutral.secondary"
                {...labelBold}
                id={`${idPrefix}-threaded-message-thread-info__${props.id}__${idPostfix}`}
              >
                Replies
              </ChakraText>
            )}
            {props.isNewReply && (
              <MessageDotIndicator
                id={`${idPrefix}-threaded-message-new-message-indicator__${props.id}__${idPostfix}`}
              />
            )}
            <ChakraSpacer />
            <ChakraFlex
              gridGap="8px"
              id={`${idPrefix}-threaded-message-expand-collapse-wrapper__${props.id}__${idPostfix}`}
            >
              <ChakraText
                color="primary.100"
                {...labelBold}
                id={`${idPrefix}-threaded-message-expand-collapse-label__${props.id}__${idPostfix}`}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </ChakraText>
              <UpDownArrow
                fill={primary[100]}
                height="16px"
                id={`${idPrefix}-threaded-message-expand-collapse-arrow__${props.id}__${idPostfix}`}
                width="16px"
              />
            </ChakraFlex>
          </ChakraFlex>
          {isExpanded && (
            <ChakraFlex
              alignItems={(props.replies ?? []).length > 0 ? 'start' : 'center'}
              direction="column"
              gridGap="16px"
              id={`${idPrefix}-threaded-message-expand-collapse-container__${props.id}__${idPostfix}`}
              padding="6px"
              width="100%"
            >
              {(props.replies ?? []).length > 0 ? (
                <>
                  {props.replies
                    .filter((reply) => !reply.isRemoved)
                    .map((reply) => (
                      <SingleMessage
                        avatarSize="sm"
                        channel={props.channel}
                        chatMessage={
                          reply.isFileMessage
                            ? (reply.sbMessage as FileMessage)
                            : (reply.sbMessage as UserMessage)
                        }
                        chatUserId={props.chatUserId}
                        fileMessage={
                          reply.isFileMessage
                            ? (reply.sbMessage as FileMessage)
                            : null
                        }
                        id={`${idPrefix}-threaded-message-expand-collapse-reply__${reply?.messageId?.toFixed()}__${idPostfix}`}
                        isChannelReadOnly={props.isChannelReadOnly}
                        isMessageSent={props.isMessageSent}
                        isShowKebabMenu={props.isShowKebabMenuForReplies}
                        isShowReply={false}
                        isThreadedMessage={false}
                        key={`message-tile-reply-${reply?.messageId}`}
                        noMargin
                        onMessageRemoved={(isRemoved) => {
                          if (isRemoved)
                            props.onMessageRemoved(
                              props.messageId,
                              reply?.messageId,
                            );
                        }}
                        patientId={props.patientId}
                        referenceDate={props.createdAt}
                        {...reply}
                      />
                    ))}
                  {props.isShowReply && (
                    <MentionsBox
                      channelUrl={url}
                      id={`${idPrefix}-threaded-message-mentions-box__${props.id}__${idPostfix}`}
                      isMessageSent={props.isMessageSent}
                      key={props.id}
                      members={members as ChatUser[]}
                      onEnterPressed={(newMessage) => {
                        props.onSendMessage(newMessage);
                        setFileToSend(null);
                      }}
                      onFileSelected={(selectedFile) => {
                        setFileToSend(selectedFile);
                      }}
                      onFocus={props.onInputFocus}
                      placeholder={getMentionsBoxReplyPlacholderText(
                        true,
                        isMaudProviderChatMessagesPrototypeEnabled,
                      )}
                      updatedAttachment={fileToSend}
                    />
                  )}
                  {fileToSend && (
                    <AttachmentViewer
                      attachments={[fileToSend.file]}
                      id={props.id}
                      isLoading={props.isMessageSent}
                      onRemove={() => {
                        setFileToSend(null);
                      }}
                    />
                  )}
                </>
              ) : (
                <OverviewLoading />
              )}
            </ChakraFlex>
          )}
        </ChakraFlex>
      </SingleMessage>
    );
  },
);

ThreadedMessage.displayName = 'ThreadedMessage';

interface IndividualChannelMessageProps {
  avatarSize: AvatarSize;
  chatUserId: string;
  channel: GroupChannel;
  createdAt: number;
  chatMessage: UserMessage | FileMessage;
  fileMessage?: FileMessage;
  id: string;
  isChannelReadOnly: boolean;
  isMentioned: boolean;
  isMyMessage: boolean;
  isRead: boolean;
  message: string;
  messageId: number;
  noMargin?: boolean;
  onMessageRemoved: (messageId: number) => void;
  patientId: string;
  referenceDate?: number;
  sender: User;
  timestamp: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const IndividualChannelMessage = React.forwardRef<
  HTMLDivElement,
  IndividualChannelMessageProps
>(
  (
    {
      avatarSize,
      channel,
      chatMessage,
      chatUserId,
      createdAt,
      fileMessage,
      id,
      isChannelReadOnly,
      isMentioned,
      isMyMessage,
      isRead,
      message,
      messageId,
      noMargin = false,
      onMessageRemoved,
      referenceDate,
      sender,
      timestamp,
      unreadRef,
    }: IndividualChannelMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      maudProviderChatMessagesPrototype:
        isMaudProviderChatMessagesPrototypeEnabled,
      messagePinning: isMessagePinningEnabled,
    } = useFlags<LDFlags>();
    const hasMessageReactions = chatMessage?.reactions.length > 0;
    const { emojiContainer, toggleReaction } = useChannelContext();
    const analytics = useAnalytics();

    const memberNicknamesMap = new Map(
      channel?.members.map((obj) => [obj.userId, obj.nickname]),
    );
    const singleMessageContainerProps = {
      alignItems: 'start',
      gridGap: '16px',
      marginBottom: noMargin ? '' : '24px',
      width: '100%',
    };
    const calculatedDate =
      ChatUtil.calculateRelativeDate(referenceDate, true) !==
      ChatUtil.calculateRelativeDate(createdAt, true)
        ? ChatUtil.calculateRelativeDate(createdAt, true)
        : null;
    const [isPinLoading, setIsPinLoading] = useState<boolean>(false);
    const [isMessagePinned, setIsMessagePinned] = useState<boolean>(
      channel?.pinnedMessageIds?.includes(chatMessage.messageId),
    );
    const pinOrUnpinText = `${isMessagePinned ? 'Unpin' : 'Pin'} message`;

    const onTogglePin = (messageId: number) => {
      const messageType = fileMessage
        ? MessageTypeFilter.FILE
        : MessageTypeFilter.USER;

      const getPatientIdFromChannelUrl = (channelUrl: string) => {
        const split = channelUrl.split(/[_]+/g);
        if (channelUrl.startsWith(ChannelId.ACT)) return split[1];
        else if (channelUrl.startsWith(ChannelId.PT_PR_CCT)) return split[3];
        else return split[2];
      };

      setIsPinLoading(true);

      const eventProps: MessagesEventsProps = {
        channelType: channel.url.split('_')[0],
        channelUrl: channel.url,
        messageId: messageId.toString(),
        messageType,
        patientId: getPatientIdFromChannelUrl(channel.url),
        source: AnalyticsSourceValues.PATIENT_MESSAGES_TAB,
      };

      if (isMessagePinned) {
        analytics?.trackEvent(
          AnalyticsEvents.patientChartMessageChannelUnpinMessage,
          eventProps,
        );

        channel
          .unpinMessage(messageId)
          .then(() => setIsMessagePinned(false))
          .catch((e) => console.error(e))
          .finally(() => setIsPinLoading(false));
      } else {
        analytics?.trackEvent(
          AnalyticsEvents.patientChartMessageChannelPinMessage,
          eventProps,
        );

        channel
          .pinMessage(messageId)
          .then(() => setIsMessagePinned(true))
          .catch((e) => console.error(e))
          .finally(() => setIsPinLoading(false));
      }
    };

    return (
      <ChakraFlex
        direction="row-reverse"
        id={`${idPrefix}-single-message-container__${id}__${idPostfix}`}
        ref={ref}
        {...singleMessageContainerProps}
      >
        <ChannelMembers
          id={`${idPrefix}-single-message-members__${id}__${idPostfix}`}
          members={[sender] as ChatUser[]}
          paddedTop={false}
          showPopoverOnHover
          size={avatarSize}
        />
        <ChakraFlex
          alignItems="end"
          direction="column"
          id={`${idPrefix}-single-message-wrapper__${id}__${idPostfix}`}
          width="100%"
        >
          <MessageHeaderRow
            createdDate={calculatedDate}
            id={`${idPrefix}-single-message-header-row__${id}__${idPostfix}`}
            isMentioned={isMentioned}
            isRead={isRead}
            isReverse
            ref={unreadRef}
            senderName={sender?.nickname}
            timestamp={timestamp}
          />
          <ChakraFlex
            _hover={{ background: '#ffffff !important' }}
            className="sendbird-parent-message-info"
            direction="column"
            id={`${idPrefix}-single-message-column-wrapper__${id}__${idPostfix}`}
            width="100%"
          >
            <ChakraFlex
              direction="row-reverse"
              id={`${idPrefix}-single-message-row-wrapper__${id}__${idPostfix}`}
              width="100%"
            >
              {fileMessage ? (
                <DocumentMessage
                  content={fileMessage}
                  id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                  isShowKebabMenu={isMyMessage}
                  isThreadedMessage={false}
                  onMessageRemoved={(isRemoved) => {
                    if (isRemoved) onMessageRemoved(messageId);
                  }}
                />
              ) : (
                <ChakraFlex
                  backgroundColor="primary.10"
                  borderRadius="14px 14px 0px 14px"
                  gridGap="24px"
                  maxWidth="80%"
                  padding="8px"
                >
                  <MessageContent
                    content={message}
                    id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                  />
                </ChakraFlex>
              )}
              {isMaudProviderChatMessagesPrototypeEnabled &&
                isMessagePinningEnabled &&
                !isChannelReadOnly && (
                  <TooltipWrapper
                    className="sb-tooltip-outgoing"
                    hoverTooltip={<Tooltip>{pinOrUnpinText}</Tooltip>}
                  >
                    <PatientMyMessagePin
                      chatMessage={chatMessage}
                      id={id}
                      isMessagePinned={isMessagePinned}
                      isPinLoading={isPinLoading}
                      messageType="outgoing"
                      onTogglePin={onTogglePin}
                    />
                  </TooltipWrapper>
                )}
              {!isChannelReadOnly && !hasMessageReactions && (
                <TooltipWrapper
                  className="sb-tooltip-outgoing"
                  hoverTooltip={<Tooltip>Add reaction</Tooltip>}
                >
                  <MessageItemReactionMenu
                    className="sendbird-parent-message-info__reaction-menu sb-reaction-menu-outgoing sendbird-mouse-hover"
                    emojiContainer={emojiContainer}
                    key={chatMessage?.messageId}
                    message={chatMessage}
                    spaceFromTrigger={getSpaceFromTrigger(fileMessage)}
                    toggleReaction={toggleReaction}
                    userId={chatUserId}
                  />
                </TooltipWrapper>
              )}
            </ChakraFlex>

            <ChakraFlex
              direction="row-reverse"
              id={`${id}-emoji-reactions-row-container`}
              width="100%"
            >
              {!isChannelReadOnly && hasMessageReactions && (
                <ChakraFlex
                  data-testid={`${id}__emoji-reactions-wrapper`}
                  id={`${id}__emoji-reactions-wrapper`}
                >
                  {/* Doesn't have supposedHover capabilty */}
                  <EmojiReactions
                    channel={channel}
                    emojiContainer={emojiContainer}
                    memberNicknamesMap={memberNicknamesMap}
                    message={chatMessage}
                    spaceFromTrigger={getSpaceFromTrigger(null)}
                    toggleReaction={toggleReaction}
                    userId={chatUserId}
                  />
                </ChakraFlex>
              )}
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
IndividualChannelMessage.displayName = 'IndividualChannelMessage';

interface PatientMyMessageTileProps {
  chatUserId: string;
  channel: GroupChannel;
  id: string;
  isChannelReadOnly: boolean;
  isGroupChannel: boolean;
  isMessageSent: boolean;
  message: ChatMessage;
  onInputFocus: (inputRef: React.MutableRefObject<HTMLInputElement>) => void;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
  onRepliesExpanded: (message: ChatMessage, onSuccess?: () => void) => void;
  onSendMessage: (value: ChatMessage) => void;
  patientId: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const PatientMyMessageTile = React.forwardRef<
  HTMLDivElement,
  PatientMyMessageTileProps
>(
  (
    {
      chatUserId,
      channel,
      id,
      isChannelReadOnly,
      isGroupChannel,
      isMessageSent,
      message,
      onInputFocus,
      onMessageRemoved,
      onRepliesExpanded,
      onSendMessage,
      patientId,
      unreadRef,
    }: PatientMyMessageTileProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const fileMessage = message?.isFileMessage
      ? (message?.sbMessage as FileMessage)
      : null;

    if (message?.isAdminMessage)
      return (
        <AdminMessage
          avatarSize="md"
          ref={ref}
          {...message}
          unreadRef={unreadRef}
        />
      );

    if (!isGroupChannel && message?.isMyMessage)
      return (
        <IndividualChannelMessage
          avatarSize="md"
          channel={channel}
          chatMessage={
            fileMessage != null
              ? fileMessage
              : (message.sbMessage as UserMessage)
          }
          chatUserId={chatUserId}
          fileMessage={fileMessage}
          id={id}
          isChannelReadOnly={isChannelReadOnly}
          isRead={message?.isRead && !isChannelReadOnly}
          onMessageRemoved={onMessageRemoved}
          patientId={patientId}
          ref={ref}
          referenceDate={message?.createdAt}
          unreadRef={unreadRef}
          {...message}
        />
      );

    const hasNewReply =
      !!message?.hasNewReply ||
      channel?.myLastRead < message?.threadInfo?.lastRepliedAt;

    if (message?.hasThread)
      return (
        <ThreadedMessage
          avatarSize="md"
          channel={channel}
          chatMessage={message.sbMessage as UserMessage}
          chatUserId={chatUserId}
          fileMessage={fileMessage}
          isChannelReadOnly={isChannelReadOnly}
          isMessageSent={isMessageSent}
          isNewReply={hasNewReply}
          isRead={!hasNewReply && !isChannelReadOnly}
          isShowKebabMenuForReplies={message?.isMyMessage}
          isShowReply={!isChannelReadOnly}
          messageId={message?.messageId}
          onInputFocus={onInputFocus}
          onMessageRemoved={onMessageRemoved}
          onRepliesExpanded={() => onRepliesExpanded(message)}
          onSendMessage={(newMessage) =>
            onSendMessage({
              ...newMessage,
              parentMessageId: message?.messageId,
              thread: message?.thread,
            })
          }
          patientId={patientId}
          ref={ref}
          replies={message?.thread}
          unreadRef={unreadRef}
          {...message}
        />
      );

    return (
      <SingleMessage
        avatarSize="md"
        channel={channel}
        chatMessage={message.sbMessage as UserMessage}
        chatUserId={chatUserId}
        fileMessage={fileMessage}
        id={id}
        isChannelReadOnly={isChannelReadOnly}
        isMessageSent={isMessageSent}
        isRead={message?.isRead && !isChannelReadOnly}
        isShowKebabMenu={message?.isMyMessage}
        isShowReply={isGroupChannel && !isChannelReadOnly}
        isThreadedMessage={false}
        onMessageRemoved={(isRemoved) =>
          isRemoved && onMessageRemoved(message?.messageId)
        }
        onSendMessage={(newMessage) =>
          onSendMessage({ ...newMessage, parentMessageId: message?.messageId })
        }
        patientId={patientId}
        ref={ref}
        referenceDate={message?.createdAt}
        unreadRef={unreadRef}
        {...message}
      />
    );
  },
);

PatientMyMessageTile.displayName = 'PatientMyMessageTile';

export default PatientMyMessageTile;
